import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getCooldownEnd, SCOUT_COOLDOWN_MS } from '@/lib/cooldown';

/**
 * Returns the cooldown end timestamp for the current user on a single player.
 * Null if not on cooldown. Re-fetches when refetch() is called.
 */
export function useScoutCooldown(playerId: string | null | undefined) {
  const { user } = useAuth();
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [createdAt, setCreatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!user || !playerId) {
      setEndsAt(null);
      setCreatedAt(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const since = new Date(Date.now() - SCOUT_COOLDOWN_MS).toISOString();
    const { data } = await supabase
      .from('scouting_sessions')
      .select('created_at')
      .eq('user_id', user.id)
      .eq('player_id', playerId)
      .eq('completed', true)
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const sessionCreatedAt = data?.created_at ? new Date(data.created_at) : null;
    setCreatedAt(sessionCreatedAt);
    setEndsAt(sessionCreatedAt ? getCooldownEnd(sessionCreatedAt) : null);
    setLoading(false);
  }, [user, playerId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { endsAt, createdAt, loading, refetch: fetch };
}

/**
 * Returns a Map<player_id, cooldownEnd Date> for all of the current user's
 * completed sessions in the last 24h. One round-trip.
 */
export function useScoutCooldownMap() {
  const { user } = useAuth();
  const [map, setMap] = useState<Map<string, Date>>(new Map());

  const fetch = useCallback(async () => {
    if (!user) {
      setMap(new Map());
      return;
    }
    const since = new Date(Date.now() - SCOUT_COOLDOWN_MS).toISOString();
    const { data } = await supabase
      .from('scouting_sessions')
      .select('player_id, created_at')
      .eq('user_id', user.id)
      .eq('completed', true)
      .gte('created_at', since)
      .order('created_at', { ascending: false });
    const next = new Map<string, Date>();
    for (const row of data || []) {
      if (!next.has(row.player_id)) {
        const end = getCooldownEnd(row.created_at);
        if (end) next.set(row.player_id, end);
      }
    }
    setMap(next);
  }, [user]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { cooldowns: map, refetch: fetch };
}